/**
 * Delivery state of one customer email (#3787), or the lack of one.
 *
 * The state is Resend's own report, written by its webhook. Without a row there
 * is no evidence either way, so the component says exactly that instead of
 * implying the email arrived. Colour never carries the meaning alone: every
 * state has its own text and icon.
 */
import { useTranslation } from 'react-i18next';
import { Box, Chip, Stack, Typography, type ChipProps } from '@mui/material';
import CheckCircleOutlineIcon from '@mui/icons-material/CheckCircleOutline';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import ScheduleIcon from '@mui/icons-material/Schedule';
import WarningAmberIcon from '@mui/icons-material/WarningAmber';
import type { OutboundEmail } from '@/hooks/useOutboundEmails';
import { formatDateTime } from '@/utils/locale';
import { deliveredAtToShow, emailDeliveryPresentation, type EmailDeliveryTone } from './email-delivery-status';

const TONE_COLOR: Record<EmailDeliveryTone, ChipProps['color']> = {
  success: 'success',
  info: 'info',
  warning: 'warning',
  error: 'error',
  unknown: 'default',
};

const TONE_ICON: Record<EmailDeliveryTone, ChipProps['icon']> = {
  success: <CheckCircleOutlineIcon />,
  info: <ScheduleIcon />,
  warning: <WarningAmberIcon />,
  error: <ErrorOutlineIcon />,
  unknown: <HelpOutlineIcon />,
};

interface EmailDeliveryStatusProps {
  /** The delivery row; null or undefined when nothing is tracked for the email. */
  email?: OutboundEmail | null;
}

export function EmailDeliveryStatus({ email }: EmailDeliveryStatusProps) {
  const { t } = useTranslation('common');
  const presentation = emailDeliveryPresentation(email?.status);
  const deliveredAt = email ? deliveredAtToShow(email) : null;
  const hint = email ? presentation.hintKey : 'emailDelivery.noEvidence';

  return (
    <Box data-testid="email-delivery-status" data-status={email?.status ?? 'unknown'}>
      <Stack direction="row" spacing={1} alignItems="center" flexWrap="wrap" useFlexGap>
        <Chip
          size="small"
          color={TONE_COLOR[presentation.tone]}
          variant={presentation.tone === 'unknown' ? 'outlined' : 'filled'}
          icon={TONE_ICON[presentation.tone]}
          label={t(presentation.labelKey)}
        />
        {email ? (
          <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>
            {email.recipientEmail}
          </Typography>
        ) : null}
      </Stack>
      <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
        {t(hint)}
      </Typography>
      {email ? (
        <>
          {email.subject ? (
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', overflowWrap: 'anywhere' }}>
              {t('emailDelivery.subject', { subject: email.subject })}
            </Typography>
          ) : null}
          <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
            {email.sentAt ? `${t('emailDelivery.sentAt', { time: formatDateTime(email.sentAt) })} · ` : ''}
            {deliveredAt ? `${t('emailDelivery.deliveredAt', { time: formatDateTime(deliveredAt) })} · ` : ''}
            {t('emailDelivery.updatedAt', { time: formatDateTime(email.lastEventAt) })}
          </Typography>
          {email.statusReason ? (
            <Typography
              variant="caption"
              color={presentation.tone === 'error' || presentation.tone === 'warning' ? 'error.main' : 'text.secondary'}
              sx={{ display: 'block', overflowWrap: 'anywhere' }}
            >
              {t('emailDelivery.reason', { reason: email.statusReason })}
            </Typography>
          ) : null}
        </>
      ) : null}
    </Box>
  );
}
